import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  X,
  Building2,
  Users,
  BookOpen,
  Loader2,
  Save,
  Search,
  CheckSquare,
  Square,
  FileSpreadsheet,
  School,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useDebounce } from "use-debounce";
import Select from "react-select";
import HrmDashboardBranchPicker, {
  branchSubtitle,
} from "../../hrm/shared/hrmDashboardBranchSelect";
import { getAllBranches_Array } from "../../../api/hrm/branches/info";
import { getCustomersByBranch } from "../../../api/crm/customers/customerInfo";
import {
  getAssignableCoursesForBranch,
  getCourseAssignEmployees,
  getEmployeeCourseAccess,
  updateEmployeeCourseAccess,
} from "../../../api/elearning/courses/employeeCourseAccess";
import { showErrorToast, showSuccessToast } from "../../../components/ConfirmDialog";
import {
  customerInModalSelectStyles,
  formatCustomerOptionLabel,
  filterCustomerOption,
  mapBranchCustomersToOptions,
  modalSelectPortalProps,
} from "../../../components/ui/customerSelectInModal";
import CourseAssignImportModal from "./CourseAssignImportModal";

const ASSIGN_CONTROL_HEIGHT = 52;

const schoolFilterSelectStyles = {
  ...customerInModalSelectStyles,
  control: (provided, state) => ({
    ...(typeof customerInModalSelectStyles.control === "function"
      ? customerInModalSelectStyles.control(provided, state)
      : provided),
    minHeight: ASSIGN_CONTROL_HEIGHT,
    height: ASSIGN_CONTROL_HEIGHT,
    borderRadius: "1rem",
    borderColor: state.isFocused ? "#fecaca" : "#e5e7eb",
    boxShadow: state.isFocused ? "0 0 0 2px rgba(254, 202, 202, 0.45)" : "none",
    fontSize: 13,
  }),
  valueContainer: (provided) => ({
    ...provided,
    paddingTop: 6,
    paddingBottom: 6,
    paddingLeft: 12,
    paddingRight: 8,
  }),
  indicatorsContainer: (provided) => ({
    ...provided,
    height: ASSIGN_CONTROL_HEIGHT,
  }),
  menuPortal: (base) => ({ ...base, zIndex: 200000 }),
};

const groupCoursesBySubject = (courses = []) => {
  const map = {};
  for (const c of courses) {
    const key = c.subject_id || c.subject_name || "other";
    if (!map[key]) {
      map[key] = {
        subject_id: c.subject_id,
        subject_name: c.subject_name || "Khác",
        subject_sort_order: c.subject_sort_order ?? 0,
        courses: [],
      };
    }
    map[key].courses.push(c);
  }
  return Object.values(map).sort(
    (a, b) => a.subject_sort_order - b.subject_sort_order
  );
};

const normalizeClassNames = (course) =>
  Array.isArray(course?.class_names)
    ? course.class_names.filter((x) => String(x || "").trim())
    : [];

const QuickFilterGroup = ({
  title,
  options,
  value,
  onChange,
  allLabel = "Tất cả",
  disabled = false,
}) => (
  <div className="space-y-2">
    <p className="text-[10px] font-black uppercase tracking-wide text-gray-400">
      {title}
    </p>
    <div className="flex flex-wrap gap-2">
      <button
        type="button"
        disabled={disabled}
        onClick={() => onChange("")}
        className={`rounded-full border px-3 py-1.5 text-[11px] font-bold transition-all ${
          disabled
            ? "cursor-not-allowed border-gray-100 bg-gray-50 text-gray-300"
            : !value
              ? "border-red-200 bg-red-50 text-red-700"
              : "border-gray-200 bg-white text-gray-600 hover:border-red-200 hover:text-red-600"
        }`}
      >
        {allLabel}
      </button>
      {options.map((opt) => {
        const active = value === opt.value;
        return (
          <button
            key={opt.value}
            type="button"
            disabled={disabled}
            onClick={() => onChange(active ? "" : opt.value)}
            className={`rounded-full border px-3 py-1.5 text-[11px] font-bold transition-all ${
              disabled
                ? "cursor-not-allowed border-gray-100 bg-gray-50 text-gray-300"
                : active
                  ? "border-red-200 bg-red-50 text-red-700"
                  : "border-gray-200 bg-white text-gray-600 hover:border-red-200 hover:text-red-600"
            }`}
          >
            {opt.label}
            {opt.count != null ? (
              <span className="ml-1.5 text-[10px] text-gray-400">({opt.count})</span>
            ) : null}
          </button>
        );
      })}
    </div>
  </div>
);

const CourseAssignModal = ({ isOpen, onClose, onSaved }) => {
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState("");
  const [employees, setEmployees] = useState([]);
  const [employeeTotal, setEmployeeTotal] = useState(0);
  const [employeePage, setEmployeePage] = useState(1);
  const [assignableCourses, setAssignableCourses] = useState([]);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState("");
  const [selectedCourseIds, setSelectedCourseIds] = useState([]);
  const [employeeKeyword, setEmployeeKeyword] = useState("");
  const [employeeTypeFilter, setEmployeeTypeFilter] = useState("");
  const [partnerCustomerId, setPartnerCustomerId] = useState("");
  const [branchCustomers, setBranchCustomers] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [courseKeyword, setCourseKeyword] = useState("");
  const [subjectFilter, setSubjectFilter] = useState("");
  const [levelFilter, setLevelFilter] = useState("");
  const [loadingBranches, setLoadingBranches] = useState(false);
  const [loadingBranchData, setLoadingBranchData] = useState(false);
  const [loadingEmployees, setLoadingEmployees] = useState(false);
  const [loadingMoreEmployees, setLoadingMoreEmployees] = useState(false);
  const [loadingEmployee, setLoadingEmployee] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [debouncedEmployeeKeyword] = useDebounce(employeeKeyword, 350);
  const employeeLoadMoreRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    setLoadingBranches(true);
    getAllBranches_Array({ is_active: true })
      .then((rows) => setBranches(Array.isArray(rows) ? rows : []))
      .catch(() => showErrorToast("Không tải được danh sách chi nhánh"))
      .finally(() => setLoadingBranches(false));
  }, [isOpen]);

  const branchSelectOptions = useMemo(
    () => [
      { value: "", label: "Chọn chi nhánh", sub: "Loại chi nhánh • Người đứng đầu" },
      ...branches.map((b) => ({
        value: String(b.id),
        label: b.name || "—",
        sub: branchSubtitle(b),
        affiliation_type: b.affiliation_type,
      })),
    ],
    [branches]
  );

  const schoolSelectOptions = useMemo(
    () => mapBranchCustomersToOptions(branchCustomers),
    [branchCustomers]
  );

  const loadAssignableCourses = useCallback(async (nextBranchId) => {
    if (!nextBranchId) {
      setAssignableCourses([]);
      return;
    }
    try {
      const courseRows = await getAssignableCoursesForBranch(nextBranchId);
      setAssignableCourses(courseRows);
    } catch (e) {
      setAssignableCourses([]);
      throw e;
    }
  }, []);

  const loadBranchCustomers = useCallback(async (nextBranchId) => {
    if (!nextBranchId) {
      setBranchCustomers([]);
      return;
    }
    setLoadingCustomers(true);
    try {
      const res = await getCustomersByBranch(nextBranchId);
      const list = res?.items || res || [];
      setBranchCustomers(Array.isArray(list) ? list : []);
    } catch (e) {
      setBranchCustomers([]);
      showErrorToast(
        e?.response?.data?.message || "Không tải được danh sách trường / khách hàng"
      );
    } finally {
      setLoadingCustomers(false);
    }
  }, []);

  const loadEmployees = useCallback(async (
    nextBranchId,
    {
      keyword = "",
      employeeKind = "",
      partnerCustomerId: customerId = "",
      page = 1,
      append = false,
    } = {}
  ) => {
    if (!nextBranchId) {
      setEmployees([]);
      setEmployeeTotal(0);
      setEmployeePage(1);
      return;
    }

    if (append) {
      setLoadingMoreEmployees(true);
    } else {
      setLoadingEmployees(true);
    }

    try {
      const res = await getCourseAssignEmployees(nextBranchId, {
        keyword: keyword || undefined,
        employee_kind: employeeKind || undefined,
        partner_customer_id: customerId || undefined,
        page,
        size: 40,
      });
      setEmployees((prev) => {
        if (!append) return res.employees || [];
        const seen = new Set(prev.map((x) => x.id));
        const merged = [...prev];
        (res.employees || []).forEach((row) => {
          if (!seen.has(row.id)) merged.push(row);
        });
        return merged;
      });
      setEmployeeTotal(Number(res.total || 0));
      setEmployeePage(Number(res.page || page));
    } finally {
      if (append) {
        setLoadingMoreEmployees(false);
      } else {
        setLoadingEmployees(false);
      }
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    setSelectedEmployeeId("");
    setSelectedCourseIds([]);
    setDirty(false);
    setImportOpen(false);
    setEmployeeKeyword("");
    setEmployeeTypeFilter("");
    setPartnerCustomerId("");
    setCourseKeyword("");
    setSubjectFilter("");
    setLevelFilter("");
    if (!branchId) {
      setBranchCustomers([]);
      setEmployees([]);
      setEmployeeTotal(0);
      setAssignableCourses([]);
      return;
    }
    setLoadingBranchData(true);
    Promise.all([loadAssignableCourses(branchId), loadBranchCustomers(branchId)])
      .catch((e) =>
        showErrorToast(e?.response?.data?.message || "Không tải được dữ liệu chi nhánh")
      )
      .finally(() => setLoadingBranchData(false));
  }, [branchId, isOpen, loadAssignableCourses, loadBranchCustomers]);

  useEffect(() => {
    if (!isOpen || !branchId) return;
    setSelectedEmployeeId("");
    setSelectedCourseIds([]);
    setDirty(false);
    // Khi đã chọn trường: luôn chỉ lấy GV đối tác của trường đó (bỏ qua tab Nhân viên)
    const effectiveKind = partnerCustomerId
      ? "partner_teacher"
      : employeeTypeFilter;
    loadEmployees(branchId, {
      keyword: debouncedEmployeeKeyword,
      employeeKind: effectiveKind,
      partnerCustomerId,
      page: 1,
    }).catch((e) =>
      showErrorToast(
        e?.response?.data?.message || "Không tải được danh sách nhân sự"
      )
    );
  }, [
    branchId,
    debouncedEmployeeKeyword,
    partnerCustomerId,
    employeeTypeFilter,
    isOpen,
    loadEmployees,
  ]);

  useEffect(() => {
    if (!isOpen || !branchId) return;
    const node = employeeLoadMoreRef.current;
    if (!node) return;
    const hasMore = employees.length < employeeTotal;
    if (!hasMore || loadingEmployees || loadingMoreEmployees) return;

    const effectiveKind = partnerCustomerId
      ? "partner_teacher"
      : employeeTypeFilter;

    const observer = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (!first?.isIntersecting) return;
        loadEmployees(branchId, {
          keyword: debouncedEmployeeKeyword,
          employeeKind: effectiveKind,
          partnerCustomerId,
          page: employeePage + 1,
          append: true,
        }).catch((e) =>
          showErrorToast(
            e?.response?.data?.message || "Không tải thêm được danh sách nhân sự"
          )
        );
      },
      { rootMargin: "120px" }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [
    branchId,
    debouncedEmployeeKeyword,
    partnerCustomerId,
    employeePage,
    employeeTotal,
    employeeTypeFilter,
    employees.length,
    isOpen,
    loadEmployees,
    loadingEmployees,
    loadingMoreEmployees,
  ]);

  const loadEmployeeAccess = useCallback(async (employeeId) => {
    if (!employeeId) {
      setSelectedCourseIds([]);
      setDirty(false);
      return;
    }
    setLoadingEmployee(true);
    try {
      const data = await getEmployeeCourseAccess(employeeId);
      setSelectedCourseIds(data?.course_ids || []);
      setDirty(false);
    } catch (e) {
      setSelectedCourseIds([]);
      showErrorToast(
        e?.response?.data?.message || "Không tải được khóa học của nhân viên"
      );
    } finally {
      setLoadingEmployee(false);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    loadEmployeeAccess(selectedEmployeeId);
  }, [selectedEmployeeId, isOpen, loadEmployeeAccess]);

  const subjectOptions = useMemo(() => {
    const map = new Map();
    assignableCourses.forEach((course) => {
      const value = String(course.subject_id || course.subject_name || "").trim();
      const label = String(course.subject_name || "Khác").trim();
      if (!value) return;
      map.set(value, { value, label, count: (map.get(value)?.count || 0) + 1 });
    });
    return [...map.values()].sort((a, b) => a.label.localeCompare(b.label, "vi"));
  }, [assignableCourses]);

  const levelOptions = useMemo(() => {
    const map = new Map();
    assignableCourses.forEach((course) => {
      const value = String(course.level || "").trim();
      if (!value) return;
      map.set(value, { value, label: value, count: (map.get(value)?.count || 0) + 1 });
    });
    return [...map.values()].sort((a, b) => a.label.localeCompare(b.label, "vi"));
  }, [assignableCourses]);

  const classOptions = useMemo(() => {
    const map = new Map();
    assignableCourses.forEach((course) => {
      normalizeClassNames(course).forEach((className) => {
        map.set(className, {
          value: className,
          label: className,
          count: (map.get(className)?.count || 0) + 1,
        });
      });
    });
    return [...map.values()].sort((a, b) => a.label.localeCompare(b.label, "vi"));
  }, [assignableCourses]);

  const groupedCourses = useMemo(() => {
    const q = courseKeyword.trim().toLowerCase();
    const list = assignableCourses.filter((c) => {
      if (
        q &&
        !String(c.name || "").toLowerCase().includes(q) &&
        !String(c.code || "").toLowerCase().includes(q) &&
        !String(c.subject_name || "").toLowerCase().includes(q)
      ) {
        return false;
      }
      if (subjectFilter) {
        const subjectValue = String(c.subject_id || c.subject_name || "").trim();
        if (subjectValue !== subjectFilter) return false;
      }
      if (levelFilter && String(c.level || "").trim() !== levelFilter) {
        return false;
      }
      return true;
    });
    return groupCoursesBySubject(list);
  }, [assignableCourses, courseKeyword, levelFilter, subjectFilter]);

  const selectedEmployee = employees.find((e) => e.id === selectedEmployeeId);
  const visibleCourseIds = groupedCourses.flatMap((group) =>
    group.courses.map((course) => course.id)
  );
  const allSelected =
    visibleCourseIds.length > 0 &&
    visibleCourseIds.every((id) => selectedCourseIds.includes(id));

  const toggleCourse = (courseId) => {
    setSelectedCourseIds((prev) => {
      const next = prev.includes(courseId)
        ? prev.filter((id) => id !== courseId)
        : [...prev, courseId];
      return next;
    });
    setDirty(true);
  };

  const toggleAllCourses = () => {
    setSelectedCourseIds((prev) => {
      if (allSelected) {
        return prev.filter((id) => !visibleCourseIds.includes(id));
      }
      return [...new Set([...prev, ...visibleCourseIds])];
    });
    setDirty(true);
  };

  const handleSave = async () => {
    if (!selectedEmployeeId) {
      showErrorToast("Vui lòng chọn nhân viên");
      return;
    }
    setSaving(true);
    try {
      await updateEmployeeCourseAccess(selectedEmployeeId, selectedCourseIds);
      showSuccessToast("Đã cập nhật khóa học cho nhân viên");
      setDirty(false);
      await loadEmployees(branchId, {
        keyword: debouncedEmployeeKeyword,
        employeeKind: partnerCustomerId ? "partner_teacher" : employeeTypeFilter,
        partnerCustomerId,
        page: 1,
      });
      onSaved?.();
    } catch (e) {
      showErrorToast(
        e?.response?.data?.message || "Không lưu được phân quyền khóa học"
      );
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    if (saving) return;
    onClose?.();
  };

  if (!isOpen) return null;

  return createPortal(
    <AnimatePresence>
      <div className="fixed inset-0 z-[140] flex items-center justify-center p-4">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-gray-900/60 backdrop-blur-[2px]"
          onClick={handleClose}
        />

        <motion.div
          initial={{ opacity: 0, scale: 0.96, y: 12 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.96, y: 12 }}
          className="relative flex w-full max-w-6xl flex-col overflow-hidden rounded-3xl border border-white bg-white shadow-2xl max-h-[92vh] md:h-[min(92vh,900px)] md:max-h-[min(92vh,900px)]"
        >
            <div className="bg-gradient-to-r from-red-700 to-red-800 px-8 py-5 text-white flex items-center justify-between shrink-0 gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="p-2.5 bg-white/15 rounded-2xl shrink-0">
                <BookOpen className="w-6 h-6" />
              </div>
              <div className="min-w-0">
                <h2 className="text-lg font-black uppercase tracking-tight">
                  Gán khóa học theo nhân viên
                </h2>
                <p className="text-xs text-red-100 mt-0.5">
                  Chọn chi nhánh → nhân viên có quyền xem khóa học → bật/tắt khóa được phép xem
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              {branchId ? (
                <button
                  type="button"
                  onClick={() => setImportOpen(true)}
                  disabled={saving}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white/15 hover:bg-white/25 text-xs font-bold transition-colors disabled:opacity-50"
                  title="Nhập Excel gán khóa hàng loạt"
                >
                  <FileSpreadsheet className="w-4 h-4" />
                  Nhập Excel
                </button>
              ) : null}
              <button
                type="button"
                onClick={handleClose}
                className="p-2 rounded-xl hover:bg-white/15 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="px-8 py-5 border-b border-gray-100 bg-gray-50/80 shrink-0">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="min-w-0">
                <label className="text-[11px] font-black text-gray-400 uppercase mb-2 flex items-center gap-1.5">
                  <Building2 size={12} /> Chi nhánh
                </label>
                {loadingBranches ? (
                  <div className="flex items-center gap-2 text-sm text-gray-500 py-2">
                    <Loader2 className="w-4 h-4 animate-spin" /> Đang tải chi nhánh...
                  </div>
                ) : (
                  <HrmDashboardBranchPicker
                    instanceId="course-assign-branch"
                    options={branchSelectOptions}
                    value={branchId || ""}
                    onChange={(val) => setBranchId(val || "")}
                    controlHeight={ASSIGN_CONTROL_HEIGHT}
                    aria-label="Chọn chi nhánh gán khóa học"
                  />
                )}
              </div>
              <div className="min-w-0">
                <label className="text-[11px] font-black text-gray-400 uppercase mb-2 flex items-center gap-1.5">
                  <School size={12} /> Trường / khách hàng
                </label>
                <Select
                  styles={schoolFilterSelectStyles}
                  isClearable
                  isLoading={loadingCustomers}
                  isDisabled={!branchId || loadingCustomers}
                  placeholder={
                    !branchId
                      ? "Chọn chi nhánh trước"
                      : loadingCustomers
                        ? "Đang tải trường..."
                        : "Chọn trường / khách hàng..."
                  }
                  noOptionsMessage={() =>
                    loadingCustomers
                      ? "Đang tải..."
                      : "Không có trường / khách hàng"
                  }
                  options={schoolSelectOptions}
                  value={
                    schoolSelectOptions.find(
                      (o) => String(o.value) === String(partnerCustomerId)
                    ) || null
                  }
                  onChange={(opt) => setPartnerCustomerId(opt?.value || "")}
                  formatOptionLabel={formatCustomerOptionLabel}
                  filterOption={filterCustomerOption}
                  {...modalSelectPortalProps}
                />
              </div>
            </div>
          </div>

          {!branchId ? (
            <div className="flex min-h-0 flex-1 items-center justify-center p-12 text-center text-gray-500">
              <div>
                <Building2 className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                <p className="font-semibold text-gray-700">Chọn chi nhánh để bắt đầu</p>
                <p className="text-sm mt-1">
                  Hệ thống sẽ liệt kê nhân viên có quyền xem khóa học trong chi nhánh đó.
                </p>
              </div>
            </div>
          ) : (
            <div className="grid min-h-0 flex-1 grid-cols-1 overflow-hidden lg:grid-cols-5">
              {/* Employees */}
              <div className="lg:col-span-2 border-r border-gray-100 flex flex-col min-h-0">
                <div className="px-5 py-4 border-b border-gray-50 shrink-0">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="text-xs font-black text-gray-500 uppercase flex items-center gap-1.5">
                      <Users size={14} /> Nhân viên ({employeeTotal})
                    </h3>
                  </div>
                  <div className="mb-3 flex flex-wrap gap-2">
                    {[
                      { value: "", label: "Tất cả" },
                      { value: "internal_employee", label: "Nhân viên" },
                      { value: "partner_teacher", label: "Giáo viên đối tác" },
                    ].map((opt) => {
                      const lockedToPartner = Boolean(partnerCustomerId);
                      const active = lockedToPartner
                        ? opt.value === "partner_teacher"
                        : employeeTypeFilter === opt.value;
                      const disabled = lockedToPartner && opt.value !== "partner_teacher";
                      return (
                        <button
                          key={opt.value || "all"}
                          type="button"
                          disabled={disabled}
                          onClick={() => {
                            if (disabled) return;
                            setEmployeeTypeFilter(opt.value);
                          }}
                          className={`rounded-full border px-3 py-1.5 text-[11px] font-bold transition-all ${
                            disabled
                              ? "cursor-not-allowed border-gray-100 bg-gray-50 text-gray-300"
                              : active
                                ? "border-red-200 bg-red-50 text-red-700"
                                : "border-gray-200 bg-white text-gray-600 hover:border-red-200 hover:text-red-600"
                          }`}
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={employeeKeyword}
                      onChange={(e) => setEmployeeKeyword(e.target.value)}
                      placeholder="Tìm tên, email, mã NV..."
                      className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-gray-200 text-sm outline-none focus:border-red-500"
                    />
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto p-3 space-y-2 bg-slate-100">
                  {loadingBranchData || loadingEmployees ? (
                    <div className="flex justify-center py-10">
                      <Loader2 className="w-6 h-6 animate-spin text-red-600" />
                    </div>
                  ) : employees.length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-8 px-4">
                      Không có dữ liệu phù hợp với bộ lọc hiện tại.
                    </p>
                  ) : (
                    employees.map((emp) => {
                      const active = emp.id === selectedEmployeeId;
                      return (
                        <button
                          key={emp.id}
                          type="button"
                          onClick={() => setSelectedEmployeeId(emp.id)}
                          className={`w-full text-left rounded-2xl border px-4 py-3 transition-all shadow-sm ${
                            active
                              ? "border-red-400 bg-red-50 ring-1 ring-red-200"
                              : "border-slate-200 bg-slate-50 hover:border-red-300 hover:bg-white"
                          }`}
                        >
                          <p className="font-bold text-sm text-gray-900 truncate">{emp.name}</p>
                          <p className="text-xs text-gray-600 truncate mt-0.5">{emp.email}</p>
                          <div className="mt-2 flex flex-wrap items-center gap-1.5">
                            <span
                              className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${
                                emp.teacher_kind === "partner_school"
                                  ? "bg-blue-100 text-blue-800 border border-blue-300"
                                  : "bg-slate-200 text-slate-700 border border-slate-300"
                              }`}
                            >
                              {emp.teacher_kind === "partner_school"
                                ? "Giáo viên đối tác"
                                : "Nhân viên"}
                            </span>
                            {emp.partner_school_name ? (
                              <span className="truncate rounded-full border border-amber-300 bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-900">
                                {emp.partner_school_name}
                              </span>
                            ) : null}
                          </div>
                          <p className="text-[10px] font-bold text-red-700 mt-2 uppercase tracking-wide">
                            {emp.assigned_count ?? 0} khóa đang được gán
                          </p>
                        </button>
                      );
                    })
                  )}
                  {!loadingBranchData && !loadingEmployees && employees.length < employeeTotal ? (
                    <div
                      ref={employeeLoadMoreRef}
                      className="flex justify-center py-4 text-xs text-gray-400"
                    >
                      {loadingMoreEmployees ? (
                        <span className="inline-flex items-center gap-2">
                          <Loader2 className="h-4 w-4 animate-spin text-red-600" />
                          Đang tải thêm...
                        </span>
                      ) : (
                        "Kéo xuống để tải thêm"
                      )}
                    </div>
                  ) : null}
                </div>
              </div>

              {/* Courses */}
              <div className="lg:col-span-3 flex flex-col min-h-0">
                <div className="px-5 py-4 border-b border-gray-50 shrink-0">
                  <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                    <h3 className="text-xs font-black text-gray-500 uppercase flex items-center gap-1.5">
                      <BookOpen size={14} />
                      {selectedEmployee
                        ? `Khóa học — ${selectedEmployee.name}`
                        : "Khóa học"}
                    </h3>
                    {selectedEmployeeId && assignableCourses.length > 0 && (
                      <button
                        type="button"
                        onClick={toggleAllCourses}
                        className="text-[11px] font-bold text-red-600 hover:text-red-800 uppercase tracking-wide flex items-center gap-1"
                      >
                        {allSelected ? <Square size={14} /> : <CheckSquare size={14} />}
                        {allSelected ? "Bỏ chọn tất cả" : "Chọn tất cả"}
                      </button>
                    )}
                  </div>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      value={courseKeyword}
                      onChange={(e) => setCourseKeyword(e.target.value)}
                      placeholder="Tìm khóa học, môn học..."
                      disabled={!selectedEmployeeId}
                      className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-gray-200 text-sm outline-none focus:border-red-500 disabled:bg-gray-50 disabled:text-gray-400"
                    />
                  </div>
                  <div className="mt-4 space-y-4">
                    <QuickFilterGroup
                      title="Môn học"
                      options={subjectOptions}
                      value={subjectFilter}
                      onChange={setSubjectFilter}
                      disabled={!selectedEmployeeId}
                    />
                    <QuickFilterGroup
                      title="Cấp học"
                      options={levelOptions}
                      value={levelFilter}
                      onChange={setLevelFilter}
                      disabled={!selectedEmployeeId}
                    />
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto p-4">
                  {!selectedEmployeeId ? (
                    <p className="text-sm text-gray-500 text-center py-12">
                      Chọn một nhân viên ở cột trái để xem và chỉnh sửa khóa học.
                    </p>
                  ) : loadingEmployee ? (
                    <div className="flex justify-center py-12">
                      <Loader2 className="w-6 h-6 animate-spin text-red-600" />
                    </div>
                  ) : groupedCourses.length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-12">
                      Không có khóa học nào có thể gán cho chi nhánh này.
                    </p>
                  ) : (
                    <div className="space-y-5">
                      {groupedCourses.map((group) => (
                        <div key={group.subject_id || group.subject_name}>
                          <p className="text-[11px] font-black text-gray-400 uppercase mb-2 px-1">
                            {group.subject_name}
                          </p>
                          <div className="space-y-2">
                            {group.courses.map((course) => {
                              const checked = selectedCourseIds.includes(course.id);
                              return (
                                <label
                                  key={course.id}
                                  className={`flex items-start gap-3 rounded-2xl border px-4 py-3 cursor-pointer transition-all ${
                                    checked
                                      ? "border-red-200 bg-red-50/60"
                                      : "border-gray-100 bg-white hover:border-gray-200"
                                  }`}
                                >
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => toggleCourse(course.id)}
                                    className="mt-1 h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500"
                                  />
                                  <div className="min-w-0 flex-1">
                                    <p className="font-semibold text-sm text-gray-900">
                                      {course.name}
                                    </p>
                                    <p className="text-xs text-gray-500 mt-0.5">
                                      {[course.code, course.level]
                                        .filter(Boolean)
                                        .join(" • ")}
                                      {!course.is_active && (
                                        <span className="ml-2 text-amber-600 font-semibold">
                                          (Ngưng dùng)
                                        </span>
                                      )}
                                    </p>
                                    {normalizeClassNames(course).length > 0 ? (
                                      <p className="mt-1 text-[11px] text-gray-400">
                                        Lớp học: {normalizeClassNames(course).join(", ")}
                                      </p>
                                    ) : null}
                                  </div>
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="px-8 py-5 border-t border-gray-100 bg-white flex items-center justify-between shrink-0">
            <p className="text-xs text-gray-500">
              {selectedEmployeeId && dirty
                ? `${selectedCourseIds.length} khóa được chọn — chưa lưu`
                : selectedEmployeeId
                  ? `${selectedCourseIds.length} khóa đang được gán`
                  : "Chưa chọn nhân viên"}
            </p>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={handleClose}
                disabled={saving}
                className="px-5 py-2.5 rounded-xl border border-gray-200 text-gray-600 font-bold text-sm hover:bg-gray-50"
              >
                Đóng
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={!selectedEmployeeId || !dirty || saving}
                className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl bg-red-700 text-white font-bold text-sm hover:bg-red-800 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                Lưu thay đổi
              </button>
            </div>
          </div>
        </motion.div>
      </div>

      <CourseAssignImportModal
        isOpen={importOpen}
        onClose={() => setImportOpen(false)}
        branchId={branchId}
        branchName={
          branches.find((b) => String(b.id) === String(branchId))?.name || ""
        }
        onSuccess={() => {
          if (!branchId) return;
          loadEmployees(branchId, {
            keyword: debouncedEmployeeKeyword,
            employeeKind: partnerCustomerId ? "partner_teacher" : employeeTypeFilter,
            partnerCustomerId,
            page: 1,
          }).catch(() => {});
          if (selectedEmployeeId) {
            loadEmployeeAccess(selectedEmployeeId).catch(() => {});
          }
          onSaved?.();
        }}
      />
    </AnimatePresence>,
    document.body
  );
};

export default CourseAssignModal;
